import { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import {
  type CandidateInfo,
} from "../client";
import ActiveGameZoneScreen from "../components/ActiveGameZoneScreen";
import { useCreateUserAction } from "../hooks/useCreateUserAction";
import { formatUserName } from "../utils/format/formatUserName";
import InsufficientPearlsModal, { InsufficientCurrencyError } from "../ui/modals/InsufficientPearlsModal";
import { useElectionSocket } from "../hooks/useElectionSocket";
// Using localStorage directly for non-onboarding data
import TalkToMayorScreen from "./TalkToMayorScreen";
import { useFloatingTooltip } from "../hooks/useFloatingTooltip";
import FloatingTooltip from "../ui/primitives/FloatingTooltip";
import { useCityHallConfetti } from "./cityHall/useCityHallConfetti";
import { useCityHallData } from "./cityHall/useCityHallData";
import { useCityHallVoting } from "./cityHall/useCityHallVoting";
import VoteHistoryModal from "./cityHall/VoteHistoryModal";
import VoteListScreen from "./cityHall/VoteListScreen";
import MainScreen from "./cityHall/MainScreen";
import { ElectionsInfoScreen, MayorInfoScreen, MayorRoleScreen } from "./cityHall/InfoScreens";
import { RegisterScreen, RegisterSuccessScreen } from "./cityHall/RegisterScreens";
import {
  CityHallBackdrop,
  CityHallCloseButton,
  CityHallForeground,
} from "./cityHall/CityHallChrome";
import { createCityHallFormatters } from "./cityHall/formatters";
import { useCityHallBackButton } from "./cityHall/useCityHallBackButton";
import { useCityHallSelection } from "./cityHall/useCityHallSelection";
import {
  createCityHallNavigation,
  createElectionAdminActions,
  createRegisterCandidateAction,
} from "./cityHall/cityHallActions";
import { useAdVoteCooldowns } from "./cityHall/useAdVoteCooldowns";
import type { ScreenType } from "./cityHall/types";

interface CityHallScreenProps {
  onClose?: () => void;
  mapObjectId?: number;
  initialScreen?: ScreenType;
}

const CityHallScreen = ({ onClose, mapObjectId, initialScreen }: CityHallScreenProps = {}) => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const { createUserAction } = useCreateUserAction();
  const hasCreatedActionRef = useRef<boolean>(false);

  const { playVoteSuccess, showConfetti, triggerConfetti } = useCityHallConfetti();

  // Real-time election vote updates via WebSocket
  useElectionSocket();

  const [currentScreen, setCurrentScreen] = useState<ScreenType>(initialScreen ?? "main");

  // Clear Telegram deep-link vote intent once the voting UI is actually showing.
  useEffect(() => {
    if (currentScreen !== "vote_list" && currentScreen !== "register") return;
    try {
      sessionStorage.removeItem("deeplink_open_cityhall_vote");
    } catch {
      /* ignore */
    }
  }, [currentScreen]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [insufficientCurrencyError, setInsufficientCurrencyError] = useState<InsufficientCurrencyError | null>(null);
  const { tooltip, showFromEvent: showTooltipFromEvent } = useFloatingTooltip();
  const [currentTime, setCurrentTime] = useState<Date>(new Date());
  const [selectedCandidateForHistory, setSelectedCandidateForHistory] = useState<CandidateInfo | null>(null);
  /** Від API одразу після реєстрації; щоб не показувати екран оплати під час refetch */
  const [registerSuccessCandidateId, setRegisterSuccessCandidateId] = useState<number | null>(null);

  const {
    currentLeader,
    freePlots,
    handleEnterInterior,
    isLoadingState,
    refetch,
    sortedCandidates,
    state,
    userData,
  } = useCityHallData({
    mapObjectId,
    onClose,
    queryClient,
  });

  // Створюємо екшен для квесту "Visit City Hall" при відкритті екрану (фоново, тільки один раз)
  useEffect(() => {
    if (hasCreatedActionRef.current) return;
    hasCreatedActionRef.current = true;
    createUserAction('visit_city_hall').catch((error) => {
      console.error("Failed to create city hall action:", error);
    });
  }, [createUserAction]);

  const {
    adControllerRef,
    adCooldowns,
    handleAdVote,
    isAdCooldown,
    votingCandidateId,
  } = useAdVoteCooldowns({
    playVoteSuccess,
    queryClient,
    refetch,
    setError,
    t,
    triggerConfetti,
  });

  useCityHallBackButton({ currentScreen, navigate, onClose, setCurrentScreen });

  // (b) Refresh "now" when election schedule changes — during render
  const nextElectionKey = state?.next_election_start_date;
  const [prevNextElectionKey, setPrevNextElectionKey] = useState(nextElectionKey);
  if (nextElectionKey !== prevNextElectionKey) {
    setPrevNextElectionKey(nextElectionKey);
    setCurrentTime(new Date());
  }

  // (c) Second-resolution timer for countdowns — setState only in interval callback
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(interval);
  }, [nextElectionKey]);

  const {
    formatDate,
    formatTimeRemaining,
    formatElectionTimeRemaining,
    formatNextElectionTimeRemaining,
  } = createCityHallFormatters({ i18n, t, currentTime });

  const { handleClose, handleGoToFreePlot } = createCityHallNavigation({ freePlots, navigate, onClose });

  const handleRegister = createRegisterCandidateAction({
    queryClient,
    refetch,
    setCurrentScreen,
    setError,
    setInsufficientCurrencyError,
    setIsLoading,
    setRegisterSuccessCandidateId,
    state,
    t,
    triggerConfetti,
  });

  const {
    handleVoteClick,
    handleStarsVote,
    starsVotingCandidateId,
  } = useCityHallVoting({
    // Legacy getSelectedCandidateId was only for dead vote_count screen
    getSelectedCandidateId: () => null,
    playVoteSuccess,
    queryClient,
    refetch,
    setError,
    setInsufficientCurrencyError,
    state,
    t,
    triggerConfetti,
    userData,
  });

  const { handleCompleteElection, handleRemoveMayor } = createElectionAdminActions({
    queryClient,
    refetch,
    setCurrentScreen,
    setError,
    setIsLoading,
    t,
  });

  const {
    currentUserCandidateId,
    registerSuccessDisplayCandidateId,
  } = useCityHallSelection({ registerSuccessCandidateId, state, userData });

  // Обробники для tooltip (як в рейтингах)
  const handleShowTooltip = (e: React.MouseEvent, candidate: CandidateInfo) => {
    const text = formatUserName(candidate).displayName;
    showTooltipFromEvent(e, text);
  };

  return (
    <ActiveGameZoneScreen>
      <CityHallBackdrop showConfetti={showConfetti} />
      <div className="relative z-[1] flex h-full w-full flex-col items-center">

      {/* Close button - тільки на головному екрані */}
      {currentScreen === "main" && (
        <CityHallCloseButton handleClose={handleClose} />
      )}

      {/* Main screen */}
      {currentScreen === "main" && (
        <MainScreen
          formatElectionTimeRemaining={formatElectionTimeRemaining}
          freePlots={freePlots}
          handleCompleteElection={handleCompleteElection}
          handleEnterInterior={handleEnterInterior}
          handleGoToFreePlot={handleGoToFreePlot}
          handleRemoveMayor={handleRemoveMayor}
          isLoading={isLoading}
          setCurrentScreen={setCurrentScreen}
          state={state}
          t={t}
          userData={userData}
        />
      )}

      {/* Register screen */}
      {currentScreen === "register" && (
        <RegisterScreen
          currentUserCandidateId={currentUserCandidateId}
          error={error}
          formatDate={formatDate}
          formatNextElectionTimeRemaining={formatNextElectionTimeRemaining}
          handleRegister={handleRegister}
          isLoading={isLoading}
          setCurrentScreen={setCurrentScreen}
          state={state}
          t={t}
        />
      )}

      {/* Register success screen */}
      {currentScreen === "register_success" && (
        <RegisterSuccessScreen
          formatDate={formatDate}
          registerSuccessDisplayCandidateId={registerSuccessDisplayCandidateId}
          setCurrentScreen={setCurrentScreen}
          setRegisterSuccessCandidateId={setRegisterSuccessCandidateId}
          state={state}
          t={t}
        />
      )}

      {/* Vote list screen */}
      {currentScreen === "vote_list" && (
        <VoteListScreen
          adControllerRef={adControllerRef}
          adCooldowns={adCooldowns}
          currentLeader={currentLeader}
          currentTime={currentTime}
          currentUserCandidateId={currentUserCandidateId}
          error={error}
          formatDate={formatDate}
          formatNextElectionTimeRemaining={formatNextElectionTimeRemaining}
          handleAdVote={handleAdVote}
          handleShowTooltip={handleShowTooltip}
          handleStarsVote={handleStarsVote}
          handleVoteClick={handleVoteClick}
          isAdCooldown={isAdCooldown}
          isLoading={isLoading}
          isLoadingState={isLoadingState}
          setCurrentScreen={setCurrentScreen}
          setSelectedCandidateForHistory={setSelectedCandidateForHistory}
          sortedCandidates={sortedCandidates}
          starsVotingCandidateId={starsVotingCandidateId}
          state={state}
          t={t}
          votingCandidateId={votingCandidateId}
        />
      )}

      {/* Mayor info screen */}
      {currentScreen === "mayor_info" && (
        <MayorInfoScreen
          formatDate={formatDate}
          formatElectionTimeRemaining={formatElectionTimeRemaining}
          formatTimeRemaining={formatTimeRemaining}
          setCurrentScreen={setCurrentScreen}
          state={state}
          t={t}
        />
      )}

      {/* Mayor role screen */}
      {currentScreen === "mayor_role" && (
        <MayorRoleScreen
          setCurrentScreen={setCurrentScreen}
          t={t}
        />
      )}

      {/* Elections info — always mount when selected; never blank while state loads */}
      {currentScreen === "elections_info" && (
        state ? (
          <ElectionsInfoScreen
            setCurrentScreen={setCurrentScreen}
            state={state}
            t={t}
          />
        ) : (
          <div className="absolute inset-0 z-[10004] flex items-center justify-center [font-family:Lato,sans-serif] text-[18px] text-white">
            {t("Loading...")}
          </div>
        )
      )}

      {/* Backdrop gradients, foreground gradients and the elder character */}
      <CityHallForeground currentScreen={currentScreen} />
      </div>
      
      <InsufficientPearlsModal
        error={insufficientCurrencyError}
        onClose={() => setInsufficientCurrencyError(null)}
        requiredLabel={
          currentScreen === "register"
            ? t("To register as a candidate you need:")
            : t("To vote you need:")
        }
        onGetPearls={() => {
          setInsufficientCurrencyError(null);
          navigate('/shop?category=pearls');
        }}
      />

      <FloatingTooltip tooltip={tooltip} zIndex={10070} />

      <VoteHistoryModal
        formatDate={formatDate}
        selectedCandidateForHistory={selectedCandidateForHistory}
        setSelectedCandidateForHistory={setSelectedCandidateForHistory}
        t={t}
      />
      {/* Talk to Mayor — portal to body so ActiveGameZone aspect-ratio cannot clip/flex-break it */}
      {currentScreen === "talk_to_mayor" &&
        createPortal(
          <div className="fixed inset-0 z-[10060] flex h-[100dvh] w-screen flex-col bg-talk-to-mayor-screen-surface">
            <TalkToMayorScreen
              onBack={() => setCurrentScreen("main")}
              mayorPhotoUrl={state?.mayor?.photo_url ?? null}
              mayorVipUntil={state?.mayor?.vip_until ?? null}
            />
          </div>,
          document.body,
        )}
    </ActiveGameZoneScreen>
  );
};

export default CityHallScreen;
